import React from 'react';
import {includes} from 'lodash';
import {flow} from 'lodash/fp';

import ProfileAttribute from '/component/base/ProfileAttribute';
import ProfileSection from '/component/partial/profile/ProfileSection';
import {UserProfileModal} from '/constant/TestIDs';
import {
  PROFILE_FIELD_ACTIVATION_DATE,
  PROFILE_FIELD_BUSINESS_COACH,
  PROFILE_FIELD_CERTIFIED,
  PROFILE_FIELD_HIGHEST_RANK,
  PROFILE_FIELD_OPTAVIA_ID,
  PROFILE_FIELD_RENEWAL_EXPIRY,
} from '/enum/profileField.enum';
import {withTranslation} from '/provider/TranslationProvider';
import {setStringToClipboard} from '/util/clipboard.util';
import {generateCallback} from '/util/component.util';
import {getDateUtilDefaultInstance} from '/util/date.util';
import {showCopiedToClipboardToast} from '/util/toast.util';

import {styles} from './ProfileDetailsSection.style';

interface ProfileDetailsSectionProps {
  tt: (key: any, value: string) => any;
  fields: string[];
  businessCoachName: string;
  highestRankId: number;
  highestRankDescription: string;
  isCertified: boolean;
  renewExpiry: string;
  onBusinessCoachPress: () => void;
  activationDate: string;
  loggedInCoach: boolean;
  optaviaId: string | number;
}

export const ProfileDetailsSection = ({
  tt = withTranslation.ttDefaultProp,
  fields = [PROFILE_FIELD_OPTAVIA_ID],
  businessCoachName = '',
  highestRankId = 0,
  highestRankDescription = '',
  isCertified = false,
  renewExpiry = '',
  onBusinessCoachPress = () => {},
  activationDate = '',
  loggedInCoach = false,
  optaviaId = '',
}: ProfileDetailsSectionProps) => {
  const copyToClipboard = flow(
    // @ts-expect-error TS(2552): Cannot find name 'toString'. Did you mean 'String'... Remove this comment to see the full error message
    toString,
    setStringToClipboard,
    showCopiedToClipboardToast,
  );
  return (
    <ProfileSection
      analyticsTitle="Details"
      style={{base: styles.base}}
      title={tt('heading', 'Details')}>
      {loggedInCoach && (
        <ProfileAttribute
          isDetails
          testID={UserProfileModal.optaviaId}
          onLongPress={generateCallback({
            func: copyToClipboard,
            params: optaviaId,
          })}
          iconName="id"
          label={'OPTAVIA ID'}
          value={`${optaviaId}`}
          style={{base: styles.profileAttribute}}
        />
      )}
      {includes(fields, PROFILE_FIELD_ACTIVATION_DATE) && (
        <ProfileAttribute
          isDetails
          iconName="calendar"
          label={'Activation Date'}
          value={getDateUtilDefaultInstance().formatDate(
            activationDate,
            'MMMM d, yyyy',
          )}
          style={{base: styles.profileAttribute}}
          testID={UserProfileModal.activationDate}
        />
      )}
      {includes(fields, PROFILE_FIELD_BUSINESS_COACH) && (
        <ProfileAttribute
          isDetails
          testID={UserProfileModal.profileBusinessCoach}
          iconName="id"
          label={'Business Coach'}
          value={`${businessCoachName ? businessCoachName : 'N/A'}`}
          onPress={onBusinessCoachPress}
          style={{base: styles.profileAttribute}}
        />
      )}
      {includes(fields, PROFILE_FIELD_HIGHEST_RANK) && (
        <ProfileAttribute
          isDetails
          testID={UserProfileModal.profileHighestRank}
          iconName="star"
          label={'Highest Rank Achieved'}
          value={highestRankId ? highestRankDescription : 'N/A'}
          style={{base: styles.profileAttribute}}
        />
      )}
      {includes(fields, PROFILE_FIELD_RENEWAL_EXPIRY) && (
        <ProfileAttribute
          isDetails
          testID={UserProfileModal.profileRenewal}
          iconName="recent"
          label={'Coach Business Renewal Expiration'}
          value={getDateUtilDefaultInstance().formatDate(
            renewExpiry,
            'MMMM d, yyyy',
          )}
          style={{base: styles.profileAttribute}}
        />
      )}
      {includes(fields, PROFILE_FIELD_CERTIFIED) && (
        <ProfileAttribute
          isDetails
          testID={UserProfileModal.profileCertified}
          iconName="certified"
          label={isCertified ? 'Certified' : 'Not Certified'}
          style={{base: styles.profileAttributeCertified}}
        />
      )}
    </ProfileSection>
  );
};

export default ProfileDetailsSection;
